/**
 * Infinite Scroll for Collection Pages
 * - Observes the last product card
 * - Shows skeleton loading cards while fetching
 * - Animates new products in with a slide-up + fade effect
 */

(function () {
  'use strict';

  let observer = null;
  let isLoading = false;
  let skeletonItems = [];

  /* ─── Helpers ─────────────────────────────────────────── */

  function getNextUrl() {
    const t = document.getElementById('infinite-scroll-trigger');
    return t ? t.dataset.nextUrl : null;
  }

  function getProductGrid() {
    return document.getElementById('product-grid');
  }

  function getLastRealItem() {
    const grid = getProductGrid();
    if (!grid) return null;
    const items = grid.querySelectorAll('.grid__item:not(.is-skeleton)');
    return items.length ? items[items.length - 1] : null;
  }

  function getColumnCount() {
    const grid = getProductGrid();
    if (!grid) return 4;
    // Detect desktop columns from class e.g. grid--4-col-desktop
    const match = [...grid.classList].join(' ').match(/grid--(\d+)-col-desktop/);
    return match ? parseInt(match[1], 10) : 4;
  }

  function removeTrigger() {
    const t = document.getElementById('infinite-scroll-trigger');
    if (t) t.remove();
    disconnectObserver();
  }

  /* ─── Observer ────────────────────────────────────────── */

  function disconnectObserver() {
    if (observer) { observer.disconnect(); observer = null; }
  }

  function observeLastItem() {
    disconnectObserver();
    if (!getNextUrl()) return;

    const last = getLastRealItem();
    if (!last) return;

    observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadNextPage();
      },
      { root: null, rootMargin: '0px 0px 0px 0px', threshold: 0.1 }
    );

    observer.observe(last);
    console.log('[InfiniteScroll] Observer attached to last item.');
  }

  /* ─── Skeleton Cards ──────────────────────────────────── */

  function injectSkeletonStyles() {
    if (document.getElementById('infinite-scroll-styles')) return;
    const style = document.createElement('style');
    style.id = 'infinite-scroll-styles';
    style.textContent = `
      /* ── Separator ─────────────────────────── */
      .infinite-scroll-separator {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
        padding: 24px 0 16px;
        grid-column: 1 / -1;
        opacity: 0;
        animation: is-fade-in 0.4s ease forwards;
      }
      .infinite-scroll-separator::before,
      .infinite-scroll-separator::after {
        content: '';
        flex: 1;
        height: 1px;
        background: currentColor;
        opacity: 0.15;
      }
      .infinite-scroll-separator span {
        font-size: 11px;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        opacity: 0.5;
        white-space: nowrap;
      }

      /* ── Skeleton card ──────────────────────── */
      .is-skeleton {
        pointer-events: none;
        user-select: none;
      }
      .skeleton-card-inner {
        border-radius: 4px;
        overflow: hidden;
      }
      .skeleton-img {
        width: 100%;
        aspect-ratio: 3 / 4;
        border-radius: 4px;
        background: linear-gradient(90deg,
          rgba(128,128,128,0.1) 25%,
          rgba(128,128,128,0.2) 50%,
          rgba(128,128,128,0.1) 75%
        );
        background-size: 200% 100%;
        animation: skeleton-shimmer 1.4s ease-in-out infinite;
      }
      .skeleton-line {
        height: 12px;
        border-radius: 6px;
        margin-top: 12px;
        background: linear-gradient(90deg,
          rgba(128,128,128,0.1) 25%,
          rgba(128,128,128,0.2) 50%,
          rgba(128,128,128,0.1) 75%
        );
        background-size: 200% 100%;
        animation: skeleton-shimmer 1.4s ease-in-out infinite;
      }
      .skeleton-line--short { width: 55%; margin-top: 8px; }
      .skeleton-line--price  { width: 35%; margin-top: 10px; height: 14px; }

      /* ── New‑item entrance ──────────────────── */
      .is-new-product {
        animation: is-slide-up 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
      }

      /* ── Keyframes ──────────────────────────── */
      @keyframes skeleton-shimmer {
        0%   { background-position:  200% 0; }
        100% { background-position: -200% 0; }
      }
      @keyframes is-slide-up {
        from { opacity: 0; transform: translateY(28px); }
        to   { opacity: 1; transform: translateY(0);    }
      }
      @keyframes is-fade-in {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
    `;
    document.head.appendChild(style);
  }

  function buildSkeletonItem(delay) {
    const li = document.createElement('li');
    li.className = 'grid__item is-skeleton';
    li.innerHTML = `
      <div class="skeleton-card-inner">
        <div class="skeleton-img"></div>
        <div class="skeleton-line" style="animation-delay:${delay}ms"></div>
        <div class="skeleton-line skeleton-line--short" style="animation-delay:${delay + 80}ms"></div>
        <div class="skeleton-line skeleton-line--price" style="animation-delay:${delay + 160}ms"></div>
      </div>`;
    return li;
  }

  function buildSeparator() {
    const li = document.createElement('li');
    li.className = 'grid__item infinite-scroll-separator-wrapper';
    li.style.gridColumn = '1 / -1';
    li.innerHTML = `<div class="infinite-scroll-separator"><span>Yeni Ürünler</span></div>`;
    return li;
  }

  function showSkeletons() {
    const grid = getProductGrid();
    if (!grid) return;

    const cols = getColumnCount();
    skeletonItems = [];

    // Separator line
    const sep = buildSeparator();
    grid.appendChild(sep);
    skeletonItems.push(sep);

    // One row of skeleton cards
    for (let i = 0; i < cols; i++) {
      const sk = buildSkeletonItem(i * 80);
      grid.appendChild(sk);
      skeletonItems.push(sk);
    }
  }

  function removeSkeletons() {
    skeletonItems.forEach((el) => el.remove());
    skeletonItems = [];
  }

  /* ─── Append real items ───────────────────────────────── */

  function appendItems(newItems) {
    const grid = getProductGrid();
    if (!grid) return;

    newItems.forEach((item, i) => {
      item.classList.remove('scroll-trigger', 'animate--slide-in');
      item.classList.add('is-new-product');
      item.style.animationDelay = `${i * 50}ms`;
      grid.appendChild(item);
    });
  }

  /* ─── Core fetch ──────────────────────────────────────── */

  async function loadNextPage() {
    const nextUrl = getNextUrl();
    if (!nextUrl || isLoading) return;

    isLoading = true;
    disconnectObserver();
    showSkeletons();

    console.log('[InfiniteScroll] Fetching:', nextUrl);

    try {
      const grid = getProductGrid();
      const sectionId = grid ? grid.dataset.id : null;
      const fetchUrl = sectionId
        ? (nextUrl.includes('?') ? `${nextUrl}&section_id=${sectionId}` : `${nextUrl}?section_id=${sectionId}`)
        : nextUrl;

      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error('HTTP ' + response.status);

      const html = await response.text();
      const doc  = new DOMParser().parseFromString(html, 'text/html');

      const newItems  = Array.from(doc.querySelectorAll('#product-grid .grid__item'));
      const newTrigger = doc.getElementById('infinite-scroll-trigger');

      console.log('[InfiniteScroll] Loaded', newItems.length, 'items');

      // Update next URL
      const currentTrigger = document.getElementById('infinite-scroll-trigger');
      if (newTrigger && newTrigger.dataset.nextUrl) {
        if (currentTrigger) currentTrigger.dataset.nextUrl = newTrigger.dataset.nextUrl;
      } else {
        removeTrigger();
      }

      // Remove skeletons, add real items
      removeSkeletons();
      if (newItems.length) appendItems(newItems);

      // Keep observing if more pages exist
      if (getNextUrl()) observeLastItem();

    } catch (err) {
      console.error('[InfiniteScroll] Error:', err);
      removeSkeletons();
      observeLastItem(); // retry on scroll
    } finally {
      isLoading = false;
    }
  }

  /* ─── Init / Re‑init ──────────────────────────────────── */

  function init() {
    disconnectObserver();
    removeSkeletons();
    isLoading = false;

    injectSkeletonStyles();

    if (!getProductGrid()) return;
    if (!getNextUrl()) { console.log('[InfiniteScroll] No next page.'); return; }

    console.log('[InfiniteScroll] Ready. Next:', getNextUrl());
    observeLastItem();
  }

  // DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify section reload
  document.addEventListener('shopify:section:load', init);

  // Facet/filter AJAX replaces ProductGridContainer innerHTML → re‑init
  document.addEventListener('DOMContentLoaded', () => {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    new MutationObserver(() => setTimeout(init, 120))
      .observe(container, { childList: true, subtree: false });
  });

})();
